import { makeVar } from '@apollo/client';

export type CashFundCurrencyPrompt = Readonly<{
  /**
   * Identity of what this prompt is about — property plus currency pair. A click-away
   * dismissal is remembered against this key, so navigating to another hotel shows the
   * prompt again. Without it the guest top bar, which survives client-side navigation,
   * would stay hidden for the rest of the session.
   */
  key: string;
  /** Full sentence, already translated by the writer. */
  message: string;
  /** Action link label, already translated. */
  actionLabel: string;
  /** Persists the dismissal, then re-prices the page in the fund's currency (reloads). */
  onSwitch: () => void;
  /**
   * Persists the dismissal — a server-side consent write, per user and per event, that
   * survives every future session and device. Only for deliberate acts (opening the
   * picker), never for a stray click away.
   */
  onDismissForGood: () => void;
}>;

/**
 * The pending "switch currency to use your honeymoon fund" prompt, or `null` for none.
 *
 * A reactive variable rather than a context because the writer and the reader sit in
 * different apps and different parts of the tree: the hotel PDP decides whether a prompt is
 * due (it owns the single experiment-flag read), while the currency picker that anchors it
 * lives either in the PDP header (`@apps/stays`) or in the guest app's top bar
 * (`@apps/guest`) — and `@apps/X` may not import `@apps/Y`.
 *
 * Copy arrives already translated so this stays free of any i18n namespace, and so the
 * strings live next to the surface that owns them.
 */
export const cashFundCurrencyPromptVar = makeVar<CashFundCurrencyPrompt | null>(null);
